import React, { useState, useRef, useEffect } from 'react';
import { Moon, Sun, LogOut, ChevronDown, Plus, Minus, Check, BookOpen, X, BarChart3, Crown, MessageSquarePlus, CreditCard, Loader2, HelpCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { User } from '../../types/auth';
import { useThemeStore, getCurrentTheme } from '../../stores/themeStore';
import { StudentProgressDashboard } from '../progress';
import { SubscriptionManager } from '../payments';
import {
  EcoCashClaimStatus,
  SubjectPaymentCheckoutStatus,
  SubjectPaymentMethod,
  formatPrice,
  getEcoCashClaimStatusLabel,
} from '../../types/payments';
import { FeedbackModal } from './FeedbackModal';
import { useTourStore } from '../../stores/tourStore';
import {
  useCreateEcoCashClaim,
  useCreateEcoCashQuote,
  useCreateSubjectAccessPaynowInitiate,
  useEcoCashClaim,
  usePaymentCapabilities,
  useSubjectAccessCheckout,
} from '../../hooks/usePayments';

interface ProfileMenuProps {
  user: User | null;
  isOpen: boolean;
  onClose: () => void;
  onLogout: () => void;
  showSubscriptionManagement?: boolean;
  subscribeSubject?: string;
  setSubscribeSubject?: (value: string) => void;
  handleSubscribeSubject?: () => void;
  unsubscribeSubject?: string;
  setUnsubscribeSubject?: (value: string) => void;
  handleUnsubscribeSubject?: (subjectId?: string) => void | Promise<void>;
  subscribableSubjects?: Array<{
    id: string;
    name: string;
    monthly_price_label?: string;
    pricing_tier?: string;
  }>;
  subscribedSubjects?: Array<{
    subject_id: string;
    name: string;
    monthly_price_label?: string;
    pricing_tier?: string;
  }>;
}

// Confirmation Modal Component (for unsubscribe only now)
const ConfirmationModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  subjectName: string;
  variant: 'subscribe' | 'unsubscribe';
}> = ({ isOpen, onClose, onConfirm, title, message, subjectName, variant }) => {
  if (!isOpen) return null;

  const isSubscribe = variant === 'subscribe';
  const gradientClass = isSubscribe
    ? 'from-cyan-500 to-blue-500'
    : 'from-orange-500 to-red-500';
  const iconBgClass = isSubscribe
    ? 'bg-cyan-500/10'
    : 'bg-orange-500/10';
  const iconColor = isSubscribe ? 'text-cyan-400' : 'text-orange-400';

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-[var(--surface-elevated)] rounded-2xl shadow-2xl border border-[var(--border-color)] w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="px-6 pt-6 pb-4">
          <div className="flex items-start gap-4">
            <div className={`w-12 h-12 rounded-xl ${iconBgClass} flex items-center justify-center flex-shrink-0`}>
              {isSubscribe ? (
                <Plus className={`w-6 h-6 ${iconColor}`} />
              ) : (
                <Minus className={`w-6 h-6 ${iconColor}`} />
              )}
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-[var(--text-primary)]">{title}</h3>
              <p className="text-sm text-[var(--text-secondary)] mt-1">{message}</p>
            </div>
            <button
              onClick={onClose}
              className="p-1 rounded-lg hover:bg-[var(--hover-bg)] transition-colors"
            >
              <X className="w-5 h-5 text-[var(--text-secondary)]" />
            </button>
          </div>
        </div>

        {/* Subject Info */}
        <div className="px-6 py-4 bg-[var(--bg-secondary)] border-y border-[var(--border-color)]">
          <div className="flex items-center gap-3">
            <BookOpen className={`w-5 h-5 ${iconColor}`} />
            <span className="font-medium text-[var(--text-primary)]">{subjectName}</span>
          </div>
        </div>

        {/* Actions */}
        <div className="px-6 py-4 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 rounded-xl border border-[var(--border-color)] text-[var(--text-primary)] font-medium hover:bg-[var(--hover-bg)] transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              onConfirm();
              onClose();
            }}
            className={`flex-1 px-4 py-2.5 rounded-xl bg-gradient-to-r ${gradientClass} text-white font-medium hover:opacity-90 transition-opacity flex items-center justify-center gap-2`}
          >
            <Check className="w-4 h-4" />
            {isSubscribe ? 'Subscribe' : 'Unsubscribe'}
          </button>
        </div>
      </div>
    </div>
  );
};

const claimStatusClasses: Record<EcoCashClaimStatus, string> = {
  [EcoCashClaimStatus.PENDING_RECEIPT]: 'bg-amber-500/15 text-amber-300 border-amber-500/25',
  [EcoCashClaimStatus.MATCHED_PENDING_ALLOCATION]: 'bg-blue-500/15 text-blue-300 border-blue-500/25',
  [EcoCashClaimStatus.APPROVED]: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/25',
  [EcoCashClaimStatus.INSUFFICIENT_AMOUNT]: 'bg-orange-500/15 text-orange-300 border-orange-500/25',
  [EcoCashClaimStatus.DUPLICATE_TRANSACTION]: 'bg-rose-500/15 text-rose-300 border-rose-500/25',
  [EcoCashClaimStatus.ALREADY_USED]: 'bg-rose-500/15 text-rose-300 border-rose-500/25',
  [EcoCashClaimStatus.MANUAL_REVIEW]: 'bg-violet-500/15 text-violet-300 border-violet-500/25',
  [EcoCashClaimStatus.REJECTED]: 'bg-slate-500/15 text-slate-300 border-slate-500/25',
};

const subjectCheckoutStatusLabel: Record<SubjectPaymentCheckoutStatus, string> = {
  [SubjectPaymentCheckoutStatus.AWAITING_PAYMENT]: 'Waiting for provider confirmation',
  [SubjectPaymentCheckoutStatus.AWAITING_RECEIPT]: 'Waiting for EcoCash receipt',
  [SubjectPaymentCheckoutStatus.PROCESSING]: 'Processing payment',
  [SubjectPaymentCheckoutStatus.COMPLETED]: 'Completed',
  [SubjectPaymentCheckoutStatus.FAILED]: 'Needs attention',
};

const subjectCheckoutStatusClasses: Record<SubjectPaymentCheckoutStatus, string> = {
  [SubjectPaymentCheckoutStatus.AWAITING_PAYMENT]: 'bg-blue-500/15 text-blue-300 border-blue-500/25',
  [SubjectPaymentCheckoutStatus.AWAITING_RECEIPT]: 'bg-amber-500/15 text-amber-300 border-amber-500/25',
  [SubjectPaymentCheckoutStatus.PROCESSING]: 'bg-cyan-500/15 text-cyan-300 border-cyan-500/25',
  [SubjectPaymentCheckoutStatus.COMPLETED]: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/25',
  [SubjectPaymentCheckoutStatus.FAILED]: 'bg-rose-500/15 text-rose-300 border-rose-500/25',
};

const EcoCashSubjectModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onClaimApproved: (subjectId: string) => void;
  subjectId: string;
  subjectName: string;
  availableMethods: SubjectPaymentMethod[];
}> = ({ isOpen, onClose, onClaimApproved, subjectId, subjectName, availableMethods }) => {
  const [transactionId, setTransactionId] = useState('');
  const [submittedClaimId, setSubmittedClaimId] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<SubjectPaymentMethod>(availableMethods[0] ?? SubjectPaymentMethod.ECOCASH);
  const [paynowSetupError, setPaynowSetupError] = useState<string | null>(null);
  const [approvalHandled, setApprovalHandled] = useState(false);
  const quoteMutation = useCreateEcoCashQuote();
  const claimMutation = useCreateEcoCashClaim();
  const paynowInitiateMutation = useCreateSubjectAccessPaynowInitiate();
  const {
    data: claimStatus,
    refetch: refetchClaim,
    isFetching: isRefreshingClaim,
  } = useEcoCashClaim(submittedClaimId, Boolean(submittedClaimId) && isOpen);
  const [paynowCheckoutReference, setPaynowCheckoutReference] = useState('');
  const {
    data: paynowCheckoutStatus,
    refetch: refetchPaynowCheckout,
    isFetching: isRefreshingPaynowCheckout,
  } = useSubjectAccessCheckout(
    paynowCheckoutReference,
    Boolean(paynowCheckoutReference) && isOpen
  );

  const quote = quoteMutation.data;
  const activeClaim = claimStatus ?? claimMutation.data;
  const activePaynowCheckout = paynowCheckoutStatus ?? paynowInitiateMutation.data?.checkout;
  const amountDue = quote?.amount_due ?? 0;
  const walletBalance = quote?.wallet_balance ?? 0;
  const quoteError =
    (quoteMutation.error as any)?.response?.data?.detail ||
    (quoteMutation.error as any)?.message ||
    null;
  const claimError =
    (claimMutation.error as any)?.response?.data?.detail ||
    (claimMutation.error as any)?.message ||
    null;
  const showPaynowOption = availableMethods.includes(SubjectPaymentMethod.PAYNOW);
  const showPaymentSelector = showPaynowOption;

  useEffect(() => {
    if (!isOpen || !subjectId) return;
    setTransactionId('');
    setSubmittedClaimId('');
    setPaymentMethod(availableMethods[0] ?? SubjectPaymentMethod.ECOCASH);
    setPaynowCheckoutReference('');
    setPaynowSetupError(null);
    setApprovalHandled(false);
    quoteMutation.reset();
    claimMutation.reset();
    paynowInitiateMutation.reset();
    void quoteMutation.mutateAsync({
      items: [{ subject_id: subjectId, months: 1 }],
      use_wallet_balance: true,
    }).catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, subjectId, availableMethods]);

  useEffect(() => {
    if (approvalHandled) return;
    if (activeClaim?.claim_status === EcoCashClaimStatus.APPROVED) {
      setApprovalHandled(true);
      toast.success(`${subjectName} is now active.`);
      onClaimApproved(subjectId);
      onClose();
    }
    if (activePaynowCheckout?.status === SubjectPaymentCheckoutStatus.COMPLETED) {
      setApprovalHandled(true);
      toast.success(`${subjectName} is now active.`);
      onClaimApproved(subjectId);
      onClose();
    }
  }, [activeClaim, activePaynowCheckout, approvalHandled, onClaimApproved, onClose, subjectId, subjectName]);

  const handleSubmitEcoCash = async () => {
    if (!quote) return;
    if (amountDue > 0 && !transactionId.trim()) {
      toast.error('Enter the exact EcoCash transaction ID.');
      return;
    }

    try {
      const claim = await claimMutation.mutateAsync({
        transaction_id: amountDue > 0 ? transactionId.trim() : undefined,
        checkout_reference: quote.checkout_reference,
        items: [{ subject_id: subjectId, months: 1 }],
        use_wallet_balance: true,
      });
      setSubmittedClaimId(claim.id);
      if (claim.claim_status !== EcoCashClaimStatus.APPROVED) {
        toast.success('EcoCash claim submitted. We will keep checking its status.');
      }
    } catch (error: any) {
      const detail = error?.response?.data?.detail || 'Could not submit your EcoCash claim.';
      toast.error(detail);
    }
  };

  const handleStartPaynowCheckout = async () => {
    if (!quote) return;
    setPaynowSetupError(null);
    try {
      const response = await paynowInitiateMutation.mutateAsync({
        items: [{ subject_id: subjectId, months: 1 }],
        use_wallet_balance: true,
      });
      setPaynowCheckoutReference(response.checkout.checkout_reference);
      if (response.checkout.status === SubjectPaymentCheckoutStatus.COMPLETED) {
        toast.success('Wallet balance applied successfully.');
        return;
      }
      if (response.checkout.redirect_url) {
        window.location.assign(response.checkout.redirect_url);
        return;
      }
      setPaynowSetupError(
        response.checkout.feedback_reason || 'Paynow checkout could not be prepared.'
      );
    } catch (error: any) {
      setPaynowSetupError(
        error?.response?.data?.detail ||
          error?.message ||
          'Could not prepare Paynow checkout.'
      );
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-[var(--surface-elevated)] rounded-2xl shadow-2xl border border-[var(--border-color)] w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="px-6 pt-6 pb-4">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500/20 to-blue-500/20 flex items-center justify-center flex-shrink-0">
              <BookOpen className="w-6 h-6 text-cyan-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-[var(--text-primary)]">Subscribe to Subject</h3>
              <p className="text-sm text-[var(--text-secondary)] mt-1">
                Get full access to lessons, assessments, and AI tutoring for this subject.
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-1 rounded-lg hover:bg-[var(--hover-bg)] transition-colors"
            >
              <X className="w-5 h-5 text-[var(--text-secondary)]" />
            </button>
          </div>
        </div>

        {/* Subject & Price Info */}
        <div className="px-6 py-4 bg-[var(--bg-secondary)] border-y border-[var(--border-color)]">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <BookOpen className="w-5 h-5 text-cyan-400" />
              <span className="font-medium text-[var(--text-primary)]">{subjectName}</span>
            </div>
            <div className="text-right">
              <span className="text-2xl font-bold text-[var(--text-primary)]">
                {formatPrice(quote?.required_amount ?? 400, quote?.currency ?? 'USD')}
              </span>
              <span className="text-sm text-[var(--text-secondary)]">/month</span>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
            <div className="rounded-xl border border-[var(--border-color)] bg-[var(--surface-elevated)] px-3 py-2">
              <p className="text-[var(--text-secondary)]">Wallet balance</p>
              <p className="mt-1 font-semibold text-[var(--text-primary)]">
                {formatPrice(walletBalance, quote?.currency ?? 'USD')}
              </p>
            </div>
            <div className="rounded-xl border border-[var(--border-color)] bg-[var(--surface-elevated)] px-3 py-2">
              <p className="text-[var(--text-secondary)]">Amount due now</p>
              <p className="mt-1 font-semibold text-[var(--text-primary)]">
                {formatPrice(amountDue, quote?.currency ?? 'USD')}
              </p>
            </div>
          </div>

          {quoteError && !quote && (
            <div className="mt-4 rounded-xl border border-rose-500/25 bg-rose-500/10 px-3 py-3 text-sm text-rose-200">
              <p className="font-medium">Payment setup failed</p>
              <p className="mt-1">{quoteError}</p>
              <button
                onClick={() =>
                  void quoteMutation.mutateAsync({
                    items: [{ subject_id: subjectId, months: 1 }],
                    use_wallet_balance: true,
                  }).catch(() => undefined)
                }
                className="mt-3 text-sm font-medium text-rose-100 underline underline-offset-4"
              >
                Retry quote
              </button>
            </div>
          )}

          {showPaymentSelector && (
            <div className="mt-4 rounded-xl border border-[var(--border-color)] bg-[var(--surface-elevated)] p-2">
              <div className={`grid gap-2 ${showPaynowOption ? 'grid-cols-2' : 'grid-cols-1'}`}>
                <button
                  onClick={() => setPaymentMethod(SubjectPaymentMethod.ECOCASH)}
                  className={`rounded-xl px-3 py-2 text-sm font-medium transition-colors ${
                    paymentMethod === SubjectPaymentMethod.ECOCASH
                      ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white'
                      : 'text-[var(--text-secondary)] hover:bg-[var(--hover-bg)]'
                  }`}
                >
                  Pay with EcoCash
                </button>
                {showPaynowOption && (
                  <button
                    onClick={() => setPaymentMethod(SubjectPaymentMethod.PAYNOW)}
                    className={`rounded-xl px-3 py-2 text-sm font-medium transition-colors ${
                      paymentMethod === SubjectPaymentMethod.PAYNOW
                        ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white'
                        : 'text-[var(--text-secondary)] hover:bg-[var(--hover-bg)]'
                    }`}
                  >
                    Pay with Paynow
                  </button>
                )}
              </div>
            </div>
          )}

          {quote && (
            <div className="mt-4 rounded-xl border border-[var(--border-color)] bg-[var(--surface-elevated)] px-3 py-3 text-sm text-[var(--text-secondary)] space-y-2">
              {paymentMethod === SubjectPaymentMethod.ECOCASH ? (
                <>
                  <p className="font-medium text-[var(--text-primary)]">EcoCash instructions</p>
                  <p>{quote.pricing_snapshot.instructions}</p>
                  {quote.pricing_snapshot.merchant_code && (
                    <p>
                      Merchant code:{' '}
                      <span className="font-semibold text-[var(--text-primary)]">
                        {quote.pricing_snapshot.merchant_code}
                      </span>
                    </p>
                  )}
                  <p className="text-xs text-[var(--text-secondary)]">
                    Reference: <span className="font-mono">{quote.checkout_reference}</span>
                  </p>
                </>
              ) : (
                <>
                  <p className="font-medium text-[var(--text-primary)]">Paynow checkout</p>
                  <p>
                    Continue to the hosted Paynow payment page to pay the amount due now. Once
                    Paynow confirms, subject access is activated using the same wallet allocation
                    rules as EcoCash.
                  </p>
                  <p className="text-xs text-[var(--text-secondary)]">
                    Checkout reference: <span className="font-mono">{quote.checkout_reference}</span>
                  </p>
                </>
              )}
            </div>
          )}

          {paymentMethod === SubjectPaymentMethod.ECOCASH && amountDue > 0 && (
            <div className="mt-4">
              <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
                EcoCash transaction ID
              </label>
              <input
                value={transactionId}
                onChange={(e) => setTransactionId(e.target.value.toUpperCase())}
                placeholder="e.g. RT12345678"
                className="w-full rounded-xl border border-[var(--border-color)] bg-[var(--surface-elevated)] px-3 py-2.5 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-secondary)] outline-none focus:border-cyan-500"
              />
              <p className="mt-2 text-xs text-[var(--text-secondary)]">
                Enter the exact transaction ID from the EcoCash confirmation SMS.
              </p>
            </div>
          )}

          {paymentMethod === SubjectPaymentMethod.ECOCASH && claimError && (
            <div className="mt-4 rounded-xl border border-rose-500/25 bg-rose-500/10 px-3 py-3 text-sm text-rose-200">
              {claimError}
            </div>
          )}

          {paymentMethod === SubjectPaymentMethod.PAYNOW && paynowSetupError && (
            <div className="mt-4 rounded-xl border border-rose-500/25 bg-rose-500/10 px-3 py-3 text-sm text-rose-200">
              {paynowSetupError}
            </div>
          )}

          {activeClaim && (
            <div className="mt-4 rounded-xl border border-[var(--border-color)] bg-[var(--surface-elevated)] px-3 py-3">
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm font-medium text-[var(--text-primary)]">Claim status</span>
                <span
                  className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-medium ${claimStatusClasses[activeClaim.claim_status]}`}
                >
                  {getEcoCashClaimStatusLabel(activeClaim.claim_status)}
                </span>
              </div>
              {activeClaim.feedback_reason && (
                <p className="mt-2 text-sm text-[var(--text-secondary)]">{activeClaim.feedback_reason}</p>
              )}
              {submittedClaimId && activeClaim.claim_status !== EcoCashClaimStatus.APPROVED && (
                <button
                  onClick={() => refetchClaim()}
                  disabled={isRefreshingClaim}
                  className="mt-3 text-sm font-medium text-cyan-300 hover:text-cyan-200 disabled:opacity-60"
                >
                  {isRefreshingClaim ? 'Refreshing...' : 'Refresh claim status'}
                </button>
              )}
            </div>
          )}

          {activePaynowCheckout && paymentMethod === SubjectPaymentMethod.PAYNOW && (
            <div className="mt-4 rounded-xl border border-[var(--border-color)] bg-[var(--surface-elevated)] px-3 py-3">
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm font-medium text-[var(--text-primary)]">Paynow status</span>
                <span
                  className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-medium ${subjectCheckoutStatusClasses[activePaynowCheckout.status]}`}
                >
                  {subjectCheckoutStatusLabel[activePaynowCheckout.status]}
                </span>
              </div>
              {activePaynowCheckout.feedback_reason && (
                <p className="mt-2 text-sm text-[var(--text-secondary)]">
                  {activePaynowCheckout.feedback_reason}
                </p>
              )}
              <button
                onClick={() => refetchPaynowCheckout()}
                disabled={isRefreshingPaynowCheckout}
                className="mt-3 text-sm font-medium text-emerald-300 hover:text-emerald-200 disabled:opacity-60"
              >
                {isRefreshingPaynowCheckout ? 'Refreshing...' : 'Refresh Paynow status'}
              </button>
            </div>
          )}

          {/* Features */}
          <div className="mt-4 space-y-2">
            <div className="flex items-center gap-2 text-sm text-[var(--text-secondary)]">
              <Check className="w-4 h-4 text-emerald-400" />
              <span>30 days of full access</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-[var(--text-secondary)]">
              <Check className="w-4 h-4 text-emerald-400" />
              <span>AI-powered tutoring sessions</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-[var(--text-secondary)]">
              <Check className="w-4 h-4 text-emerald-400" />
              <span>Progress tracking & assessments</span>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="px-6 py-4 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 rounded-xl border border-[var(--border-color)] text-[var(--text-primary)] font-medium hover:bg-[var(--hover-bg)] transition-colors"
          >
            Cancel
          </button>
          {paymentMethod === SubjectPaymentMethod.ECOCASH ? (
            <button
              onClick={handleSubmitEcoCash}
              disabled={quoteMutation.isPending || claimMutation.isPending || !quote}
              className="flex-1 px-4 py-2.5 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-medium hover:opacity-90 transition-opacity flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {quoteMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Loading...
                </>
              ) : claimMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  {amountDue > 0 ? 'Submit EcoCash Claim' : 'Use Wallet Balance'}
                </>
              )}
            </button>
          ) : paymentMethod === SubjectPaymentMethod.PAYNOW ? (
            <button
              onClick={handleStartPaynowCheckout}
              disabled={quoteMutation.isPending || paynowInitiateMutation.isPending || !quote}
              className="flex-1 px-4 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-medium hover:opacity-90 transition-opacity flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {quoteMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Loading...
                </>
              ) : paynowInitiateMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Preparing...
                </>
              ) : (
                <>
                  <CreditCard className="w-4 h-4" />
                  {amountDue > 0 ? 'Continue to Paynow' : 'Use Wallet Balance'}
                </>
              )}
            </button>
          ) : (
            <button
              onClick={handleStartPaynowCheckout}
              disabled={quoteMutation.isPending || paynowInitiateMutation.isPending || !quote}
              className="flex-1 px-4 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-medium hover:opacity-90 transition-opacity flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {quoteMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Loading...
                </>
              ) : paynowInitiateMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Preparing...
                </>
              ) : (
                <>
                  <CreditCard className="w-4 h-4" />
                  {amountDue > 0 ? 'Continue to Paynow' : 'Use Wallet Balance'}
                </>
              )}
            </button>
          )}
        </div>

        {/* Security note */}
        <div className="px-6 pb-4">
          <p className="text-xs text-center text-[var(--text-secondary)]">
            {paymentMethod === SubjectPaymentMethod.ECOCASH
              ? 'EcoCash claims are only approved after the merchant receipt is matched.'
              : 'Paynow subject payments are confirmed by provider callback and status polling before access is activated.'}
          </p>
        </div>
      </div>
    </div>
  );
};

// Custom Dropdown Component
const CustomDropdown: React.FC<{
  label: string;
  icon: React.ReactNode;
  options: Array<{ id: string; name: string; monthly_price_label?: string; pricing_tier?: string }>;
  onSelect: (id: string, name: string) => void;
  variant: 'subscribe' | 'unsubscribe';
}> = ({ label, icon, options, onSelect, variant }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (id: string, name: string) => {
    onSelect(id, name);
    setIsOpen(false);
  };

  const gradientClass = variant === 'subscribe' 
    ? 'from-cyan-500/10 to-blue-500/10 border-cyan-500/20 hover:border-cyan-500/40' 
    : 'from-orange-500/10 to-red-500/10 border-orange-500/20 hover:border-orange-500/40';
  
  const iconColor = variant === 'subscribe' ? 'text-cyan-400' : 'text-orange-400';

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl border bg-gradient-to-r ${gradientClass} transition-all duration-200`}
      >
        <div className="flex items-center gap-2">
          <span className={iconColor}>{icon}</span>
          <span className="text-sm font-medium text-[var(--text-primary)]">{label}</span>
        </div>
        <ChevronDown className={`h-4 w-4 text-[var(--text-secondary)] transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-[var(--surface-elevated)] border border-[var(--border-color)] rounded-xl shadow-xl z-50 overflow-hidden max-h-48 overflow-y-auto">
          {options.length > 0 ? (
            options.map((option) => (
              <button
                key={option.id}
                onClick={() => handleSelect(option.id, option.name)}
                className="w-full px-3 py-2.5 text-left text-sm flex items-center justify-between gap-3 hover:bg-[var(--hover-bg)] transition-colors text-[var(--text-primary)]"
              >
                <div className="min-w-0 flex items-center gap-2">
                  <BookOpen className="h-4 w-4 text-[var(--text-secondary)] flex-shrink-0" />
                  <span className="truncate">{option.name}</span>
                </div>
                {option.monthly_price_label && (
                  <span className="flex-shrink-0 text-xs text-[var(--text-secondary)]">
                    {option.monthly_price_label}
                  </span>
                )}
              </button>
            ))
          ) : (
            <div className="px-3 py-3 text-sm text-[var(--text-secondary)] text-center">
              No subjects available
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const ProfileMenu: React.FC<ProfileMenuProps> = ({
  user,
  isOpen,
  onClose,
  onLogout,
  showSubscriptionManagement = false,
  subscribeSubject = '',
  setSubscribeSubject,
  handleSubscribeSubject,
  unsubscribeSubject = '',
  setUnsubscribeSubject,
  handleUnsubscribeSubject,
  subscribableSubjects = [],
  subscribedSubjects = []
}) => {
  const { startTour } = useTourStore();
  const toggleTheme = useThemeStore((state) => state.toggleTheme);
  const currentTheme = useThemeStore(getCurrentTheme);
  const { data: paymentCapabilities } = usePaymentCapabilities(Boolean(user));
  const availableSubjectMethods =
    paymentCapabilities?.subject_access_methods?.length
      ? paymentCapabilities.subject_access_methods
      : [SubjectPaymentMethod.ECOCASH];
  const subscriptionsPublicEnabled = Boolean(paymentCapabilities?.subscriptions_public_enabled);
  
  // Progress dashboard modal state
  const [showProgressDashboard, setShowProgressDashboard] = useState(false);
  
  // Subscription manager modal state
  const [showSubscriptionManager, setShowSubscriptionManager] = useState(false);
  
  // Subject payment modal state
  const [paymentModal, setPaymentModal] = useState<{
    isOpen: boolean;
    subjectId: string;
    subjectName: string;
  }>({
    isOpen: false,
    subjectId: '',
    subjectName: ''
  });
  
  // Feedback modal state
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  
  // Confirmation modal state (for unsubscribe)
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    type: 'subscribe' | 'unsubscribe';
    subjectId: string;
    subjectName: string;
  }>({
    isOpen: false,
    type: 'subscribe',
    subjectId: '',
    subjectName: ''
  });

  const getInitials = (user?: User): string => {
    if (!user?.firstName || !user?.lastName) return '...';
    return `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase();
  };

  const userInitials = user ? getInitials(user) : '...';
  // Show payment modal for subscribing
  const handleSubscribeSelect = (subjectId: string, subjectName: string) => {
    setPaymentModal({
      isOpen: true,
      subjectId,
      subjectName
    });
  };

  // Show confirmation modal for unsubscribing
  const handleUnsubscribeSelect = (subjectId: string, subjectName: string) => {
    setConfirmModal({
      isOpen: true,
      type: 'unsubscribe',
      subjectId,
      subjectName
    });
  };

  // Handle unsubscribe confirmation
  const handleConfirmAction = () => {
    setUnsubscribeSubject?.(confirmModal.subjectId);
    void handleUnsubscribeSubject?.(confirmModal.subjectId);
  };

  const closePaymentModal = () => {
    setPaymentModal(prev => ({ ...prev, isOpen: false }));
  };

  const closeConfirmModal = () => {
    setConfirmModal(prev => ({ ...prev, isOpen: false }));
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40" 
        onClick={onClose} 
      />
      
      {/* Profile Menu */}
      <div className="absolute right-0 mt-2 w-[400px] bg-[var(--surface-elevated)] rounded-lg shadow-xl border-2 border-[var(--accent-primary)]/30 py-2 z-50 backdrop-blur-md">
        {/* Profile Menu Content */}
        <div className="px-4 py-3 border-b border-[var(--border-color)] flex items-center justify-between">
          <div>
            <p className="font-semibold text-[var(--text-primary)]">
              {user ? `${user.firstName} ${user.lastName}` : 'Loading...'}
            </p>
            <p className="text-sm text-[var(--text-secondary)] mt-1">
              {user?.email ?? 'Loading...'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-[var(--bg-secondary)] hover:bg-[var(--bg-tertiary)] flex items-center justify-center border-2 border-[var(--accent-primary)]/30 shadow-md"
          >
            <span className="text-sm font-medium text-[var(--text-primary)]">{userInitials}</span>
          </button>
        </div>

        {/* Theme Selection */}
        <div className="px-4 py-3 flex items-center justify-between hover:bg-[var(--hover-bg)] transition-colors">
          <div className="flex items-center gap-2 text-[var(--text-secondary)]">
            {currentTheme === 'dark' ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
            <span className="text-sm">Theme</span>
          </div>
          <button
            onClick={() => toggleTheme()}
            className="flex items-center gap-2 bg-[var(--bg-tertiary)] text-[var(--text-primary)] rounded-lg px-3 py-1.5 text-sm font-medium hover:bg-[var(--bg-quaternary)] border border-[var(--border-color)] transition-colors"
          >
            {currentTheme === 'dark' ? (
              <>
                <Moon className="h-3.5 w-3.5" />
                <span>Dark</span>
              </>
            ) : (
              <>
                <Sun className="h-3.5 w-3.5" />
                <span>Light</span>
              </>
            )}
          </button> 
        </div>

        {/* Progress Dashboard - only show for students (USER role) */}
        {showSubscriptionManagement && (
          <button
            onClick={() => setShowProgressDashboard(true)}
            className="w-full px-4 py-3 flex items-center gap-2 text-[var(--text-secondary)] hover:bg-[var(--hover-bg)] transition-colors border-t border-[var(--border-color)]"
          >
            <BarChart3 className="h-4 w-4 text-cyan-400" />
            <span className="text-sm font-medium">My Progress</span>
            <span className="ml-auto text-xs bg-cyan-500/20 text-cyan-400 px-2 py-0.5 rounded-full">New</span>
          </button>
        )}
        
        {/* Subscription/Billing - show for all users */}
        {showSubscriptionManagement && subscriptionsPublicEnabled && (
          <button
            onClick={() => setShowSubscriptionManager(true)}
            className="w-full px-4 py-3 flex items-center gap-2 text-[var(--text-secondary)] hover:bg-[var(--hover-bg)] transition-colors"
          >
            <Crown className="h-4 w-4 text-amber-400" />
            <span className="text-sm font-medium">Subscription</span>
          </button>
        )}

        {/* Subscription Management - only show for chat interface */}
        {showSubscriptionManagement && (
          <div className="px-4 py-3 border-t border-[var(--border-color)]">
            <div className="flex flex-col gap-3">
              <CustomDropdown
                label="Subscribe to Subject"
                icon={<Plus className="h-4 w-4" />}
                options={subscribableSubjects.map((s) => ({
                  id: s.id,
                  name: s.name,
                  monthly_price_label: s.monthly_price_label,
                  pricing_tier: s.pricing_tier,
                }))}
                onSelect={handleSubscribeSelect}
                variant="subscribe"
              />

              <CustomDropdown
                label="Unsubscribe from Subject"
                icon={<Minus className="h-4 w-4" />}
                options={subscribedSubjects.map((s) => ({
                  id: s.subject_id,
                  name: s.name,
                  monthly_price_label: s.monthly_price_label,
                  pricing_tier: s.pricing_tier,
                }))}
                onSelect={handleUnsubscribeSelect}
                variant="unsubscribe"
              />
            </div>
          </div>
        )}

        {/* Feedback Button */}
        <button
          onClick={() => setShowFeedbackModal(true)}
          className="w-full px-4 py-3 flex items-center gap-2 text-[var(--text-secondary)] hover:bg-[var(--hover-bg)] transition-colors border-t border-[var(--border-color)]"
        >
          <MessageSquarePlus className="h-4 w-4 text-emerald-400" />
          <span className="text-sm font-medium">Send Feedback</span>
        </button>

        {/* Restart Tour Button */}
        <button 
          onClick={() => {
            onClose();
            startTour();
          }}
          className="w-full px-4 py-2 text-sm font-medium text-[var(--text-primary)] hover:bg-[var(--bg-secondary)] flex items-center gap-2 border-t border-[var(--border-color)]"
        >
          <HelpCircle className="h-4 w-4 text-[var(--text-secondary)]" />
          <span>Restart Tour</span>
        </button>

        {/* Logout Button */}
        <button 
          onClick={onLogout}
          className="w-full px-4 py-2 text-sm font-medium text-[var(--text-red)] hover:bg-red-400/10 flex items-center gap-2 border-t border-[var(--border-color)]"
        >
          <LogOut className="h-4 w-4" />
          <span>Sign Out</span>
        </button>
      </div>

      {/* Subject Payment Modal */}
      <EcoCashSubjectModal
        isOpen={paymentModal.isOpen}
        onClose={closePaymentModal}
        onClaimApproved={(subjectId) => {
          setSubscribeSubject?.(subjectId);
          setTimeout(() => handleSubscribeSubject?.(), 0);
        }}
        subjectId={paymentModal.subjectId}
        subjectName={paymentModal.subjectName}
        availableMethods={availableSubjectMethods}
      />

      {/* Unsubscribe Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        onClose={closeConfirmModal}
        onConfirm={handleConfirmAction}
        title="Unsubscribe from Subject"
        message="Are you sure you want to unsubscribe from this subject? You will no longer receive updates."
        subjectName={confirmModal.subjectName}
        variant="unsubscribe"
      />

      {/* Progress Dashboard Modal */}
      {showProgressDashboard && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setShowProgressDashboard(false)}
          />
          
          {/* Modal Content */}
          <div className="relative bg-[var(--surface-elevated)] rounded-2xl shadow-2xl border border-[var(--border-color)] w-full max-w-4xl max-h-[85vh] overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="h-full overflow-y-auto p-6">
              <StudentProgressDashboard onClose={() => setShowProgressDashboard(false)} />
            </div>
          </div>
        </div>
      )}
      
      {/* Subscription Manager Modal */}
      <SubscriptionManager
        isOpen={showSubscriptionManager}
        onClose={() => setShowSubscriptionManager(false)}
      />

      {/* Feedback Modal */}
      <FeedbackModal
        isOpen={showFeedbackModal}
        onClose={() => setShowFeedbackModal(false)}
      />
    </>
  );
}; 
